import type { Medication } from "./api";

/**
 * Check if a medication with the same name (case-insensitive) and same type
 * already exists on the patient's profile for this visit.
 */
export function checkDuplicate(
    existingMeds: Medication[],
    newName: string,
    isSupplement: boolean
): Medication | null {
    const normalized = newName.trim().toLowerCase();
    return (
        existingMeds.find(
            (m) =>
                m.medicationName.trim().toLowerCase() === normalized &&
                m.isSupplement === isSupplement &&
                !m.isRemoved
        ) ?? null
    );
}

/**
 * The frequency chips offered when there are no AI suggestions to show.
 *
 * These must be values the server's medication vocabulary actually produces,
 * because anything else is rewritten the moment it is saved and the nurse sees
 * her choice change under her. "At bedtime" used to be on this list and is no
 * longer a frequency at all — it normalizes to "Once daily" plus an
 * administration note of "At bedtime." Picking it stored one thing and
 * displayed another.
 *
 * Ordered by how often each appears in the production corpus.
 */
export const STANDARD_FREQUENCIES = [
    "Once daily",
    "Twice daily",
    "3 times daily",
    "Every 12 hours",
    "Every 8 hours",
    "As needed"
] as const;

/** True when a stored frequency is one of the chips, rather than custom text. */
export function isStandardFrequency(value: string | null | undefined): boolean {
    return !!value && (STANDARD_FREQUENCIES as readonly string[]).includes(value);
}
